import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Grid,
  IconButton /*, TextField*/,
  TextField,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AsyncSelectOption } from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect, { Value } from "../../../../../components/CustomSelect";
import Input from "../../../../../components/Input";
import NumberInput from "../../../../../components/NumberInput";
import DropZone from "../../../../../components/app/DropZone";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../components/globalModal/StyledModal";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetEobById } from "../../../../../queries/billings/eob/use-get-eob-by-id";
import { useUpdateEob } from "../../../../../queries/billings/eob/use-update-eob";
import { useUploadEob } from "../../../../../queries/billings/eob/use-upload-eob";
import { UploadEobDocumentFormatEnum } from "../../../../../services/api";
import { useInsurancePayerList } from "../../../../../queries/use-insurance-payer";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { PAYMENT_METHOD_OPTIONS } from "./constants";

interface UploadEobModalProps {
  disclosureHost: DisclosureHost;
}

const ALLOWED_EXTENSIONS = [".pdf", ".doc"];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

interface UploadEobFormValues {
  payerId: string;
  payerName: string;
  eobNumber: string;
  paymentMethod: string;
  checkEftNumber: string;
  totalPaymentAmount: string;
  statementDate: string;
  file: File | string | null;
  documentName: string;
}

const getDocumentFormat = (
  fileName: string
): UploadEobDocumentFormatEnum | undefined => {
  const ext = fileName.toLowerCase().split(".").pop();
  switch (ext) {
    case "pdf":
      return UploadEobDocumentFormatEnum.Pdf;
    case "doc":
      return UploadEobDocumentFormatEnum.MsWord;
    default:
      return undefined;
  }
};

const validationSchema = Yup.object({
  payerId: Yup.string().required("Payer Name is required"),
  eobNumber: Yup.string()
    .required("EOB # is required")
    .test("no-only-spaces", "EOB # cannot contain only spaces", (value) => {
      if (!value) return false;
      return value.trim().length > 0;
    }),
  // paymentMethod: Yup.string().required("Payment Method is required"),
  checkEftNumber: Yup.string(),
  totalPaymentAmount: Yup.string()
    .required("Total Payment Amount is required")
    .test("is-positive", "Payment Amount must be greater than $0", (value) => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),
  // statementDate: Yup.string()
  // .required("Statement Date is required")
  // .test(
  //   "not-future",
  //   "Statement Date cannot be a future date",
  //   (value) => {
  //     if (!value) return false;
  //     return dayjs(value).isBefore(dayjs().add(1, "day").startOf("day"));
  //   }
  // ),
  file: Yup.mixed()
    .nullable()
    .test(
      "fileType",
      "Unsupported file format. Please upload a PDF or DOC file",
      (value: any) => {
        if (!value) return true;
        if (typeof value === "string" && value.startsWith("https")) return true;
        const fileName = value.name?.toLowerCase();
        return ALLOWED_EXTENSIONS.some((ext) => fileName?.endsWith(ext));
      }
    )
    .test("fileSize", "File size must not exceed 20MB", (value: any) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value.size <= MAX_FILE_SIZE;
    }),
});

const UploadEobModal = ({ disclosureHost }: UploadEobModalProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [searchInsKey, setSearchInsKey] = useDebounce<string>("");
  const [payerPagination, setPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const editEobId = disclosureHost.data?.uuid as string | undefined;
  const isEditMode = !!editEobId;

  const { data: eobDetail } = useGetEobById(
    disclosureHost.opened ? editEobId : undefined
  );
  const eobData = (eobDetail as any)?.data as
    | {
        insurancePayer?: { payerId?: string; payerName?: string };
        eobNumber?: string;
        paymentMethod?: string;
        checkEftNumber?: string;
        totalPaymentAmount?: string | number;
        statementDate?: string;
        document?: string;
        documentName?: string;
        documentFormat?: string;
      }
    | undefined;

  const {
    data: insurancePayerList,
    isLoading: isPayerLoading,
    isFetching: isFetchingPayer,
  } = useInsurancePayerList({
    searchKey: searchInsKey,
    xTENANTID: getXtenantId(),
    pagination: payerPagination,
  });

  const { mutateAsync: uploadEob } = useUploadEob();
  const { mutateAsync: updateEob } = useUpdateEob();

  const formik = useFormik<UploadEobFormValues>({
    initialValues: {
      payerId: (isEditMode ? eobData?.insurancePayer?.payerId : "") ?? "",
      payerName: (isEditMode ? eobData?.insurancePayer?.payerName : "") ?? "",
      eobNumber: (isEditMode ? eobData?.eobNumber : "") ?? "",
      paymentMethod: (isEditMode ? eobData?.paymentMethod : "") ?? "",
      checkEftNumber: (isEditMode ? eobData?.checkEftNumber : "") ?? "",
      totalPaymentAmount:
        (isEditMode ? String(eobData?.totalPaymentAmount ?? "") : "") ?? "",
      statementDate: (isEditMode ? eobData?.statementDate : "") ?? "",
      file: isEditMode ? (eobData?.document ?? null) : null,
      documentName: (isEditMode ? eobData?.documentName : "") ?? "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEditMode) {
          showToastOnApiCall({
            apiCall: updateEob({
              eobId: disclosureHost.data.uuid,
              payerId: values.payerId,
              eobNumber: values.eobNumber,
              paymentMethod: values.paymentMethod,
              totalPaymentAmount: values.totalPaymentAmount,
              statementDate: values.statementDate,
              checkEftNumber: values.checkEftNumber || undefined,
            }),
            showSnackbar,
            onSave: () => {
              queryClient.invalidateQueries({
                queryKey: ["get-all-eobs"],
              });
              queryClient.invalidateQueries({
                queryKey: ["get-eob-by-id"],
              });
              closeAndReset();
            },
          });
        } else {
          // File is optional — only send the document fields when a file has
          // actually been selected.
          const file =
            values.file && typeof values.file !== "string"
              ? (values.file as File)
              : undefined;
          const documentFormat = file
            ? getDocumentFormat(file.name)
            : undefined;

          showToastOnApiCall({
            apiCall: uploadEob({
              payerId: values.payerId,
              eobNumber: values.eobNumber,
              paymentMethod: values.paymentMethod,
              totalPaymentAmount: values.totalPaymentAmount,
              statementDate: values.statementDate,
              documentFormat,
              file,
              checkEftNumber: values.checkEftNumber || undefined,
              documentName: file?.name,
            }),
            showSnackbar,
            onSave: () => {
              queryClient.invalidateQueries({
                queryKey: ["get-all-eobs"],
              });
              closeAndReset();
            },
          });
        }
      } catch (error) {
        showSnackbar(getErrorSnackbar(error));
      }
    },
  });

  const handleFileSelect = (fileList: FileList | null) => {
    const curFile = fileList?.item(0);

    formik.setFieldValue("file", curFile ?? null);
    formik.setFieldValue("documentName", curFile?.name ?? "");

    formik.validateForm();
    formik.setFieldTouched("file", !curFile);
  };

  function closeAndReset() {
    formik.resetForm();
    setSearchInsKey("");
    disclosureHost.close();
  }

  const payerOptions =
    insurancePayerList?.data?.content?.map(
      (insurance: { payerId: string; payerName: string }) => ({
        value: insurance.payerId,
        label: insurance.payerName,
      })
    ) ?? [];

  // const isUploadDisabled =
  //   !formik.values.payerId ||
  //   !formik.values.eobNumber ||
  //   !formik.values.paymentMethod ||
  //   !formik.values.totalPaymentAmount ||
  //   !formik.values.statementDate ||
  //   !formik.values.file;

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle={isEditMode ? "Update EOB" : "Add EOB"}
      dialogAction={
        <Box display="flex" gap={2} justifyContent="flex-end">
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
            sx={{ width: "100px" }}
          />
          <StyledButtonV2
            label={isEditMode ? "Update" : "Save"}
            variant="contained"
            onClick={formik.submitForm}
            sx={{ width: "100px" }}
            // disabled={isUploadDisabled}
          />
        </Box>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "750px",
          maxHeight: "90vh",
        },
      }}
    >
      <Box pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <AsyncSelectScroll
              loading={isPayerLoading || isFetchingPayer}
              pagination={payerPagination}
              setPagination={setPayerPagination}
              totalElements={insurancePayerList?.data?.page?.totalElements}
              required
              value={
                formik.values.payerId
                  ? {
                      value: formik.values.payerId,
                      label: formik.values.payerName,
                    }
                  : null
              }
              onChange={(newValue: AsyncSelectOption | null) => {
                formik.setFieldValue("payerId", newValue?.value ?? "");
                formik.setFieldValue("payerName", newValue?.label ?? "");
              }}
              onSearch={(val) => {
                setSearchInsKey(val);
                setPayerPagination((p) => ({ ...p, page: 0 }));
              }}
              enableFilterOption
              options={payerOptions}
              label="Payer Name"
              placeholder="Select Payer"
              error={
                formik.touched.payerId && (formik.errors.payerId as string)
              }
            />
          </Grid>

          <Grid item xs={4}>
            <Input
              required
              label="EOB #"
              textFieldProps={{
                placeholder: "Enter EOB Doc Number",
                name: "eobNumber",
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                value: formik.values.eobNumber,
              }}
              error={
                formik.touched.eobNumber && (formik.errors.eobNumber as string)
              }
            />
          </Grid>

          <Grid item xs={4}>
            <CustomSelect
              // required
              label="Payment Method"
              placeholder="Select Method"
              options={PAYMENT_METHOD_OPTIONS}
              value={formik.values.paymentMethod}
              onChange={(value: Value) =>
                formik.setFieldValue("paymentMethod", value ?? "")
              }
              error={
                formik.touched.paymentMethod &&
                (formik.errors.paymentMethod as string)
              }
            />
          </Grid>

          <Grid item xs={4}>
            <Input
              label="Cheque/EFT Number"
              textFieldProps={{
                placeholder: "Enter Cheque/EFT Number",
                name: "checkEftNumber",
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                value: formik.values.checkEftNumber,
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <NumberInput
              required
              label="Total Payment Amount ($)"
              min={0}
              blockedKeys={["-", "+", "e", "E"]}
              textFieldProps={{
                name: "totalPaymentAmount",
                value: formik.values.totalPaymentAmount,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                placeholder: "Enter Amount",
              }}
              error={
                formik.touched.totalPaymentAmount &&
                (formik.errors.totalPaymentAmount as string)
              }
            />
          </Grid>

          <Grid item xs={4}>
            <CustomDatePicker
              // required
              label="Statement Date"
              placeholder="MM/DD/YYYY"
              name="statementDate"
              maxDate={dayjs()}
              value={formik.values.statementDate}
              handleBlur={formik.handleBlur}
              onChange={(val) => {
                formik.setFieldTouched("statementDate", true);
                formik.setFieldValue(
                  "statementDate",
                  val ? val?.toISOString() : ""
                );
              }}
              error={
                formik.touched.statementDate &&
                (formik.errors.statementDate as string)
              }
            />
          </Grid>

          {isEditMode ? (
            <Grid item xs={12}>
              <Box mt={1}>
                <TextField
                  label="EOB File"
                  value={formik.values.documentName || "-"}
                  fullWidth
                  size="small"
                  disabled
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: formik.values.documentName ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (eobData?.document) {
                            window.open(eobData.document, "_blank");
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    ) : undefined,
                  }}
                />
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box mt={1}>
                <DropZone
                  label="Upload EOB File"
                  value={formik.values.documentName || undefined}
                  height="150px"
                  accept="all"
                  content="Click to upload EOB File"
                  onClick={handleFileSelect}
                  error={formik.touched.file && (formik.errors.file as string)}
                  supportedFile="PDF, DOC file format supported."
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </StyledFormModal>
  );
};

export default UploadEobModal;
